import { m } from "@nerves/i18n/messages";
import { Compass } from "@phosphor-icons/react";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { I18nProvider } from "react-aria-components";
import { RouteError } from "@/components/shell/RouteError";
import { buttonStyles, EmptyState, ToastRegion } from "@/components/ui";
import { useLocale } from "@/lib/locale";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function SkipLink() {
  return (
    // biome-ignore lint/a11y/useValidAnchor: skip link moves focus without touching router history
    <a
      href="#main"
      onClick={(e) => {
        e.preventDefault();
        const main = document.getElementById("main");
        main?.focus();
        main?.scrollIntoView();
      }}
      className="fixed top-2 left-2 z-[100] -translate-y-[calc(100%+24px)] rounded-control bg-primary px-3 py-2 text-sm font-medium text-primary-fg shadow-md focus:translate-y-0"
    >
      {m.skip_to_content()}
    </a>
  );
}

function RootLayout() {
  const locale = useLocale();
  return (
    <I18nProvider locale={locale}>
      <div key={locale} className="contents">
        <SkipLink />
        <Outlet />
        <ToastRegion />
      </div>
    </I18nProvider>
  );
}

function NotFound() {
  return (
    <main id="main" tabIndex={-1} className="flex min-h-dvh items-center justify-center bg-chrome">
      <EmptyState
        align="center"
        icon={Compass}
        title={m.not_found_title()}
        description={m.not_found_body()}
        action={
          <Link to="/" className={buttonStyles({ variant: "primary" })}>
            {m.go_inbox()}
          </Link>
        }
      />
    </main>
  );
}
